<template>
  <div class="bm-shopper-msg" :data-testid="`shopper-message-block-${item.id}`">
    <div class="bm-shopper-msg__lead">
      <b>Let your shopper know.</b>
      This message is not sent automatically. Copy it, send it from your own
      email, and adjust anything you like before it goes.
    </div>

    <label class="bm-shopper-msg__label" :for="`shopper-message-sender-${item.id}`">
      Your name
    </label>
    <input
      :id="`shopper-message-sender-${item.id}`"
      v-model="sender"
      type="text"
      class="bm-shopper-msg__sender"
      placeholder="Your name"
      :data-testid="`shopper-message-sender-${item.id}`"
    />

    <!--
      READONLY, NOT DISABLED: the operator must still be able to select the
      text by hand when the clipboard is unavailable, which is the fallback
      the failure message below points them to.
    -->
    <textarea
      class="bm-shopper-msg__text"
      readonly
      rows="6"
      :value="message"
      :data-testid="`shopper-message-${item.id}`"
    />

    <div class="bm-shopper-msg__actions">
      <button
        type="button"
        class="bm-btn bm-btn--sm"
        :data-testid="`copy-shopper-message-${item.id}`"
        @click="copy"
      >
        <i class="material-icons bm-btn__icon" aria-hidden="true">content_copy</i>
        Copy message
      </button>
      <span
        v-if="copyState === 'copied'"
        class="bm-shopper-msg__status"
        :data-testid="`shopper-message-copied-${item.id}`"
      >
        Copied.
      </span>
      <span
        v-else-if="copyState === 'failed'"
        class="bm-shopper-msg__status bm-shopper-msg__status--failed"
        :data-testid="`shopper-message-copy-failed-${item.id}`"
      >
        Could not copy automatically. Select the text above and copy it yourself.
      </span>
    </div>
  </div>
</template>

<script>
/**
 * Option B (Jake, 2026-09-28): the message a distributor sends their shopper
 * about an abandoned, charged order. WE DO NOT SEND IT -- this component has
 * no API call by construction. The shopper hears from the distributor, in the
 * distributor's own voice, which is the whole point of the decision.
 *
 * The sender name is prefilled with the signed-in operator (served by the
 * release-items endpoint) and stays editable; when it is unknown the field is
 * left blank for them to fill, and the message reads correctly either way.
 */
import { buildAbandonedOrderMessage } from "@/services/abandonedOrderMessage";

export default {
  name: "AbandonedShopperMessage",
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      sender: this.item.senderName || "",
      copyState: "",
    };
  },
  computed: {
    message() {
      return buildAbandonedOrderMessage({
        firstName: this.item.shopperFirstName,
        sender: this.sender,
        distributorName: this.item.distributorName,
        storeName: this.item.storeName,
        orderLabel: this.item.orderNumber,
        placedAt: this.item.placedAt,
        amountCents: this.item.chargedCents,
      });
    },
  },
  watch: {
    // A "Copied." left over from before an edit would claim the NEW text is
    // on the clipboard when it is not.
    message() {
      this.copyState = "";
    },
  },
  methods: {
    async copy() {
      try {
        await navigator.clipboard.writeText(this.message);
        this.copyState = "copied";
      } catch (err) {
        // No clipboard API (insecure context, older browser) or permission
        // denied. Say so rather than showing "Copied." over an empty
        // clipboard -- the operator would paste nothing to a waiting shopper.
        this.copyState = "failed";
      }
    },
  },
};
</script>

<style scoped>
.bm-shopper-msg {
  grid-column: 1 / -1;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--tf-surface-alt, #f1f4f7);
  font-size: 13px;
  line-height: 1.45;
}

.bm-shopper-msg__lead {
  margin-bottom: 8px;
}

.bm-shopper-msg__label {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.bm-shopper-msg__sender {
  width: 100%;
  max-width: 260px;
  margin-bottom: 8px;
  padding: 4px 8px;
  border: 1px solid var(--tf-border);
  border-radius: 6px;
  background: #fff;
}

.bm-shopper-msg__text {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--tf-border);
  border-radius: 6px;
  background: #fff;
  font: inherit;
  resize: vertical;
}

.bm-shopper-msg__actions {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 6px;
}

.bm-shopper-msg__status {
  color: var(--tf-text-muted, #6b7785);
}

.bm-shopper-msg__status--failed {
  color: var(--tf-error);
}
</style>
